import { useEffect, useRef, useState } from 'react'
import { Box, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import { useHistory } from 'react-router-dom'
import ClimateHazardIcon from '../climateAnalytics/common/ClimateHazardIcon'
import { getClimateHazardMethodology } from '../climateAnalytics/common/hazardConfig'
import siteMap from '../../siteMap'
import MethodologyContent from './MethodologyContent'
import MethodologyTOC from './MethodologyTOC'

type MethodologyDrawerProps = {
  open: boolean
  hazardCode: string | null
  anchor?: string
  onClose: () => void
}

/**
 * Right-anchored MUI Drawer that renders a hazard's methodology. Designed to
 * sit alongside (not replace) the analytics dashboards: ~560px wide so the
 * underlying chart remains visible, with a TOC strip at the top and the full
 * markdown body below. The "Open full page" affordance hands off to
 * `/methodology/:hazard#<anchor>` so users can graduate from a quick reference
 * to deep reading without losing the anchor.
 */
export default function MethodologyDrawer({ open, hazardCode, anchor, onClose }: MethodologyDrawerProps) {
  const history = useHistory()
  const methodology = hazardCode ? getClimateHazardMethodology(hazardCode) : null
  const bodyRef = useRef<HTMLDivElement | null>(null)
  // Tracks the in-drawer scroll target. Initialised from the `anchor` prop, but
  // also updated when the user clicks an entry in the in-drawer TOC.
  const [activeAnchor, setActiveAnchor] = useState<string | undefined>(anchor)

  useEffect(() => {
    setActiveAnchor(anchor)
  }, [anchor, hazardCode])

  const scrollToAnchor = (id: string) => {
    setActiveAnchor(id)
    const root = bodyRef.current
    if (!root) return
    const target = root.querySelector<HTMLElement>(`#${CSS.escape(id)}`)
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const fullPagePath =
    hazardCode != null
      ? `${siteMap.Methodology.getPath(hazardCode)}${activeAnchor ? `#${activeAnchor}` : ''}`
      : siteMap.Methodology.path

  const handleOpenFullPage = () => {
    onClose()
    history.push(fullPagePath)
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 'min(560px, 90vw)' },
          maxWidth: '100vw',
        },
      }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        {hazardCode && <ClimateHazardIcon hazardCode={hazardCode} size={32} iconSize={18} />}
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
            Methodology
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {methodology?.label ?? 'Climate hazard'}
          </Typography>
        </Stack>
        <Tooltip title="Open in full page">
          <span>
            <IconButton size="small" onClick={handleOpenFullPage} disabled={!hazardCode}>
              <OpenInNewRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close methodology drawer">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {hazardCode && methodology && methodology.sections.length > 0 && (
        <>
          <Box sx={{ px: 2, py: 1.5 }}>
            <MethodologyTOC
              hazardCode={hazardCode}
              activeAnchor={activeAnchor}
              onSelect={scrollToAnchor}
              variant="drawer"
            />
          </Box>
          <Divider />
        </>
      )}

      <Box ref={bodyRef} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        {hazardCode ? (
          <MethodologyContent hazardCode={hazardCode} anchor={activeAnchor} variant="drawer" />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a hazard to view its methodology.
          </Typography>
        )}
      </Box>
    </Drawer>
  )
}
